import React, { useState } from 'react';
import { Share2, Link2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Épuré share button — style Linear/Stripe.
 * Props:
 *   - url: string  (default: current window URL)
 *   - title: string (default: "Découvrez ce Deal ISM Parcel")
 *   - message: string (default: pre-filled promotional message)
 *   - variant: 'icon' | 'button' | 'link'  (default 'button')
 *   - className
 */
export default function ShareButton({ url, title, message, variant = 'button', className = '', testId = 'share-btn' }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = title || 'Découvrez ce Deal ISM Parcel';
  const shareMessage = message || `🔥 Deal groupé ISM Parcel — plus on est nombreux, moins ça coûte ! 👉 ${shareUrl}`;

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    // Native share on mobile if available
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareMessage, url: shareUrl });
        return;
      } catch {/* user cancelled */}
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Impossible de copier'); }
  };

  const trigger = variant === 'icon' ? (
    <button
      onClick={handleClick}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-700 shadow-sm border border-slate-200 transition-all hover:scale-105 ${className}`}
      title="Partager"
      data-testid={testId}
    >
      <Share2 className="w-4 h-4" />
    </button>
  ) : variant === 'link' ? (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors ${className}`}
      data-testid={testId}
    >
      <Share2 className="w-3.5 h-3.5" /> Partager
    </button>
  ) : (
    <Button
      onClick={handleClick}
      variant="outline"
      className={`gap-2 ${className}`}
      data-testid={testId}
    >
      <Share2 className="w-4 h-4" /> Partager
    </Button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4 sm:p-6" onClick={() => setOpen(false)} data-testid="share-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Chivo, sans-serif'}}>Partager</h3>
                <p className="text-xs text-slate-500 mt-0.5">Envoyez à vos proches, remplissez la palette plus vite</p>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 flex items-center justify-center" data-testid="share-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Grid of channels */}
            <div className="grid grid-cols-4 gap-3 mb-5">
              <ShareChannel
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank')}
                testId="share-whatsapp"
              />
              <ShareChannel
                icon={<FacebookIcon />}
                label="Facebook"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                testId="share-facebook"
              />
              <ShareChannel
                icon={<TwitterIcon />}
                label="X"
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`, '_blank')}
                testId="share-twitter"
              />
              <ShareChannel
                icon={<MessengerIcon />}
                label="Messenger"
                onClick={() => window.open(`https://www.facebook.com/dialog/send?app_id=291494419107518&link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(shareUrl)}`, '_blank')}
                testId="share-messenger"
              />
              <ShareChannel
                icon={<MailIcon />}
                label="Email"
                onClick={() => window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareMessage)}`}
                testId="share-email"
              />
              <ShareChannel
                icon={<SmsIcon />}
                label="SMS"
                onClick={() => window.location.href = `sms:?body=${encodeURIComponent(shareMessage)}`}
                testId="share-sms"
              />
              <ShareChannel
                icon={<TelegramIcon />}
                label="Telegram"
                onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareMessage)}`, '_blank')}
                testId="share-telegram"
              />
              <ShareChannel
                icon={copied ? <Check className="w-5 h-5 text-green-600" /> : <Link2 className="w-5 h-5 text-slate-600" />}
                label={copied ? 'Copié' : 'Copier'}
                onClick={copy}
                testId="share-copy"
              />
            </div>

            {/* URL preview */}
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
              <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                readOnly
                value={shareUrl}
                className="flex-1 bg-transparent text-xs text-slate-600 outline-none truncate"
                onClick={(e) => e.target.select()}
                data-testid="share-url-input"
              />
              <button
                onClick={copy}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                data-testid="share-copy-inline"
              >
                {copied ? '✓' : 'Copier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ShareChannel({ icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
      data-testid={testId}
    >
      <div className="w-11 h-11 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
        {icon}
      </div>
      <span className="text-[11px] text-slate-600 font-medium">{label}</span>
    </button>
  );
}

// Brand icons (SVG minimalists)
function WhatsAppIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;
}
function FacebookIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
}
function TwitterIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
}
function MessengerIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0084FF"><path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.652V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/></svg>;
}
function MailIcon() {
  return <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>;
}
function SmsIcon() {
  return <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{color: '#22c55e'}}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>;
}
function TelegramIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0088CC"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>;
}
